"use client";

import { useEffect, useState } from "react";
import Script from "next/script";

type DeferredAnalyticsProps = {
  googleAnalyticsId?: string | null;
  yandexMetricaId?: string | number | null;
};

export function DeferredAnalytics({
  googleAnalyticsId,
  yandexMetricaId
}: DeferredAnalyticsProps) {
  const [enabled, setEnabled] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const normalizedYandexMetricaId = yandexMetricaId
    ? String(yandexMetricaId).replace(/\D/g, "")
    : "";

  useEffect(() => {
    const syncConsent = () => {
      setConsentAccepted(localStorage.getItem("bitstone_cookie_consent") === "accepted");
    };

    syncConsent();
    window.addEventListener("bitstone-cookie-consent", syncConsent);
    return () => window.removeEventListener("bitstone-cookie-consent", syncConsent);
  }, []);

  useEffect(() => {
    if (!googleAnalyticsId || !consentAccepted) {
      return;
    }

    let activated = false;
    let fallbackId: ReturnType<typeof setTimeout> | undefined;

    const enable = () => {
      if (activated) {
        return;
      }

      activated = true;
      setEnabled(true);
    };

    const scheduleEnable = () => {
      if (activated) {
        return;
      }

      if ("requestIdleCallback" in window) {
        const idleWindow = window as Window & {
          requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
        };

        idleWindow.requestIdleCallback?.(enable, { timeout: 3000 });
        return;
      }

      fallbackId = globalThis.setTimeout(enable, 800);
    };

    const timeoutId = window.setTimeout(scheduleEnable, 20000);
    const events: Array<keyof WindowEventMap> = ["pointerdown", "touchstart", "keydown", "scroll"];

    for (const eventName of events) {
      window.addEventListener(eventName, scheduleEnable, { once: true, passive: true });
    }

    return () => {
      window.clearTimeout(timeoutId);
      if (fallbackId) {
        clearTimeout(fallbackId);
      }
      for (const eventName of events) {
        window.removeEventListener(eventName, scheduleEnable);
      }
    };
  }, [consentAccepted, googleAnalyticsId]);

  return (
    <>
      {consentAccepted && normalizedYandexMetricaId ? (
        <>
          <script
            id="yandex-metrica"
            dangerouslySetInnerHTML={{
              __html: `(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};m[i].l=1*new Date();for(var j=0;j<document.scripts.length;j++){if(document.scripts[j].src===r){return;}}k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym"); ym(${normalizedYandexMetricaId}, "init", {clickmap:true, trackLinks:true, accurateTrackBounce:true, webvisor:true});`
            }}
          />
          <noscript>
            <div>
              <img
                alt=""
                src={`https://mc.yandex.ru/watch/${normalizedYandexMetricaId}`}
                style={{ position: "absolute", left: "-9999px" }}
              />
            </div>
          </noscript>
        </>
      ) : null}

      {enabled && googleAnalyticsId ? (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
            strategy="lazyOnload"
          />
          <Script id="google-analytics" strategy="lazyOnload">
            {`window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${googleAnalyticsId}');`}
          </Script>
        </>
      ) : null}
    </>
  );
}
